import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";

function Field({ label, value, onChange, textarea, rows = 3, testId }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {textarea ? <Textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
        : <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />}
    </div>
  );
}

export default function SiteContent() {
  const [s, setS] = useState(null);
  const [brands, setBrands] = useState([]);
  const [awards, setAwards] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [authBrands, setAuthBrands] = useState([]);

  const loadAll = useCallback(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/admin/brands").then((r) => setBrands(r.data || [])).catch(() => {});
    api.get("/admin/awards").then((r) => setAwards(r.data || [])).catch(() => {});
    api.get("/admin/testimonials").then((r) => setTestimonials(r.data || [])).catch(() => {});
    api.get("/admin/authorized-brands").then((r) => setAuthBrands(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const saveSettings = async () => { try { await api.put("/admin/settings", s); toast.success("Content saved"); } catch { toast.error("Save failed"); } };

  const saveBrand = async (b) => { try { await api.put(`/admin/brands/${b.id}`, b); toast.success(`${b.name} saved`); } catch { toast.error("Save failed"); } };
  const saveAward = async (a) => { try { await api.put(`/admin/awards/${a.id}`, a); toast.success("Award saved"); loadAll(); } catch { toast.error("Save failed"); } };
  const addAward = async () => { await api.post("/admin/awards", { title: "New Award", year: "", description: "", active: true, show_homepage: false, show_about: true, show_brand: false, order: awards.length + 1 }); loadAll(); };
  const delAward = async (id) => { if (!window.confirm("Delete award?")) return; await api.delete(`/admin/awards/${id}`); loadAll(); };

  const saveTest = async (t) => { try { await api.put(`/admin/testimonials/${t.id}`, t); toast.success("Saved"); loadAll(); } catch { toast.error("Save failed"); } };
  const addTest = async () => { await api.post("/admin/testimonials", { author: "New Reviewer", location: "", rating: 5, text: "", active: true, order: testimonials.length + 1 }); loadAll(); };
  const delTest = async (id) => { if (!window.confirm("Delete testimonial?")) return; await api.delete(`/admin/testimonials/${id}`); loadAll(); };

  const saveAuth = async (b) => { try { await api.put(`/admin/authorized-brands/${b.id}`, b); toast.success("Saved"); loadAll(); } catch { toast.error("Save failed"); } };
  const addAuth = async () => { await api.post("/admin/authorized-brands", { name: "New Brand", category: "product", logo_slot: "", link: "", order: authBrands.length + 1, enabled: true }); loadAll(); };
  const delAuth = async (id) => { if (!window.confirm("Delete brand?")) return; await api.delete(`/admin/authorized-brands/${id}`); loadAll(); };

  if (!s) return <div className="text-slate-500">Loading...</div>;

  return (
    <div data-testid="admin-site-content">
      <AdminHeader title="Site Content" subtitle="Edit public-facing content, nothing here is hard-coded" />
      <Tabs defaultValue="homepage">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="homepage">Homepage</TabsTrigger>
          <TabsTrigger value="about">About / Story</TabsTrigger>
          <TabsTrigger value="brands">Brand Stories</TabsTrigger>
          <TabsTrigger value="awards">Awards</TabsTrigger>
          <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
          <TabsTrigger value="authorized">Authorized Brands</TabsTrigger>
        </TabsList>

        <TabsContent value="homepage">
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
            <Field label="Hero Headline" value={s.hero_headline} onChange={(v) => set("hero_headline", v)} testId="sc-hero-headline" />
            <Field label="Hero Subheadline" value={s.hero_subheadline} onChange={(v) => set("hero_subheadline", v)} testId="sc-hero-sub" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Primary CTA Text" value={s.hero_primary_cta} onChange={(v) => set("hero_primary_cta", v)} />
              <Field label="Secondary CTA Text" value={s.hero_secondary_cta} onChange={(v) => set("hero_secondary_cta", v)} />
            </div>
            <Field label="Boat Sales Copy" value={s.sales_body} onChange={(v) => set("sales_body", v)} textarea />
            <Field label="Marine Service Copy" value={s.service_body} onChange={(v) => set("service_body", v)} textarea />
            <Field label="Footer Tagline" value={s.footer_tagline} onChange={(v) => set("footer_tagline", v)} />
            <Button onClick={saveSettings} className="bg-mtm-purple text-white rounded-full" data-testid="save-content-button"><Save size={15} /> Save Content</Button>
          </div>
        </TabsContent>

        <TabsContent value="about">
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
            <Field label="About Headline" value={s.about_headline} onChange={(v) => set("about_headline", v)} />
            <Field label="About Body" value={s.about_body} onChange={(v) => set("about_body", v)} textarea rows={4} />
            <Field label="Why MTM Headline" value={s.about_story_headline} onChange={(v) => set("about_story_headline", v)} />
            <Field label="Why MTM Body" value={s.about_story_body} onChange={(v) => set("about_story_body", v)} textarea rows={4} />
            <Button onClick={saveSettings} className="bg-mtm-purple text-white rounded-full"><Save size={15} /> Save Content</Button>
          </div>
        </TabsContent>

        <TabsContent value="brands">
          <div className="space-y-4">
            {brands.map((b, i) => (
              <div key={b.id} className="bg-white rounded-xl border border-slate-200 p-6 space-y-3" data-testid={`sc-brand-${b.key}`}>
                <h3 className="font-heading font-bold text-lg text-mtm-purple-deep">{b.name}</h3>
                <Field label="Short Description" value={b.short_description} onChange={(v) => setBrands((arr) => arr.map((x, j) => j === i ? { ...x, short_description: v } : x))} />
                <Field label="Brand Story" value={b.story} onChange={(v) => setBrands((arr) => arr.map((x, j) => j === i ? { ...x, story: v } : x))} textarea rows={4} />
                <Button onClick={() => saveBrand(brands[i])} className="bg-mtm-purple text-white rounded-full" data-testid={`save-brand-${b.key}`}><Save size={15} /> Save {b.name}</Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="awards">
          <div className="flex justify-end mb-3"><Button onClick={addAward} className="bg-mtm-purple text-white rounded-full" data-testid="add-award-button"><Plus size={15} /> Add Award</Button></div>
          <div className="space-y-4">
            {awards.map((a, i) => (
              <div key={a.id} className="bg-white rounded-xl border border-slate-200 p-6 space-y-3" data-testid={`sc-award-${a.id}`}>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2"><Field label="Title" value={a.title} onChange={(v) => setAwards((arr) => arr.map((x, j) => j === i ? { ...x, title: v } : x))} /></div>
                  <Field label="Year" value={a.year} onChange={(v) => setAwards((arr) => arr.map((x, j) => j === i ? { ...x, year: v } : x))} />
                </div>
                <Field label="Description" value={a.description} onChange={(v) => setAwards((arr) => arr.map((x, j) => j === i ? { ...x, description: v } : x))} textarea />
                <div className="flex flex-wrap gap-5">
                  {[["active", "Active"], ["show_homepage", "Homepage"], ["show_about", "About Page"], ["show_brand", "Brand Page"]].map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 text-sm"><Switch checked={!!a[k]} onCheckedChange={(v) => setAwards((arr) => arr.map((x, j) => j === i ? { ...x, [k]: v } : x))} /> {label}</label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => saveAward(awards[i])} className="bg-mtm-purple text-white rounded-full"><Save size={15} /> Save</Button>
                  <Button variant="outline" onClick={() => delAward(a.id)} className="border-red-200 text-red-600 rounded-full"><Trash2 size={15} /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="testimonials">
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex items-center justify-between">
            <span className="text-sm font-medium">Show testimonials section on site</span>
            <Switch checked={!!s.testimonials_enabled} onCheckedChange={(v) => { set("testimonials_enabled", v); api.put("/admin/settings", { testimonials_enabled: v }).then(() => toast.success("Updated")); }} data-testid="testimonials-enabled-toggle" />
          </div>
          <div className="flex justify-end mb-3"><Button onClick={addTest} className="bg-mtm-purple text-white rounded-full" data-testid="add-testimonial-button"><Plus size={15} /> Add Testimonial</Button></div>
          <div className="space-y-4">
            {testimonials.map((t, i) => (
              <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-6 space-y-3" data-testid={`sc-test-${t.id}`}>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Author" value={t.author} onChange={(v) => setTestimonials((arr) => arr.map((x, j) => j === i ? { ...x, author: v } : x))} />
                  <Field label="Location" value={t.location} onChange={(v) => setTestimonials((arr) => arr.map((x, j) => j === i ? { ...x, location: v } : x))} />
                  <Field label="Rating (1-5)" value={t.rating} onChange={(v) => setTestimonials((arr) => arr.map((x, j) => j === i ? { ...x, rating: parseInt(v) || 5 } : x))} />
                </div>
                <Field label="Review Text" value={t.text} onChange={(v) => setTestimonials((arr) => arr.map((x, j) => j === i ? { ...x, text: v } : x))} textarea />
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm"><Switch checked={!!t.active} onCheckedChange={(v) => setTestimonials((arr) => arr.map((x, j) => j === i ? { ...x, active: v } : x))} /> Active</label>
                  <Button onClick={() => saveTest(testimonials[i])} className="bg-mtm-purple text-white rounded-full"><Save size={15} /> Save</Button>
                  <Button variant="outline" onClick={() => delTest(t.id)} className="border-red-200 text-red-600 rounded-full"><Trash2 size={15} /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="authorized">
          <div className="flex justify-end mb-3"><Button onClick={addAuth} className="bg-mtm-purple text-white rounded-full" data-testid="add-auth-brand-button"><Plus size={15} /> Add Brand</Button></div>
          <div className="space-y-3">
            {authBrands.map((b, i) => (
              <div key={b.id} className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end" data-testid={`sc-auth-${b.id}`}>
                <Field label="Name" value={b.name} onChange={(v) => setAuthBrands((arr) => arr.map((x, j) => j === i ? { ...x, name: v } : x))} />
                <div><Label className="text-xs">Category</Label><Input value={b.category} onChange={(e) => setAuthBrands((arr) => arr.map((x, j) => j === i ? { ...x, category: e.target.value } : x))} /></div>
                <Field label="Order" value={b.order} onChange={(v) => setAuthBrands((arr) => arr.map((x, j) => j === i ? { ...x, order: parseInt(v) || 0 } : x))} />
                <label className="flex items-center gap-2 text-sm"><Switch checked={!!b.enabled} onCheckedChange={(v) => setAuthBrands((arr) => arr.map((x, j) => j === i ? { ...x, enabled: v } : x))} /> Enabled</label>
                <div className="flex gap-2"><Button onClick={() => saveAuth(authBrands[i])} className="bg-mtm-purple text-white rounded-full"><Save size={15} /></Button><Button variant="outline" onClick={() => delAuth(b.id)} className="border-red-200 text-red-600 rounded-full"><Trash2 size={15} /></Button></div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
